import { __assign, __awaiter, __generator, __read, __spreadArray } from "tslib";
import { DatabaseConnectError, DatabaseUnexpectedClosedError } from '../error';
import { StorageEvent } from '../event';
import { MigrationManager } from '../migration';
import { SystemTableHelper } from '../utils/system-table-helper';
import mutex from '../utils/mutex';
var DatabaseManager = /** @class */ (function () {
    function DatabaseManager(storage, options) {
        var _a;
        var _this = this;
        // set the promise to prevent repetitive opening or closing
        this.openingMutex = mutex();
        this.closingMutex = mutex();
        this.destroyingMutex = mutex();
        /*
         * openDatabase() attempts to open the database at latest version,
         * if the open request succeed, it will emit a Ready event,
         * otherwise, the connection will fail with a Fetal event emitted.
         * if the latest version is greater than current version,
         * a migration will happen which is controlled by MigrationManager,
         **/
        this.openDatabase = function () { return __awaiter(_this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, this.openingMutex.lock(function () { return __awaiter(_this, void 0, void 0, function () {
                        var _a, name, version, schema, upgradeCallback, terminatedCallback, _b, error_1, _c, caughtErr_1, error;
                        var _this = this;
                        return __generator(this, function (_d) {
                            switch (_d.label) {
                                case 0:
                                    if (this.db)
                                        return [2 /*return*/];
                                    _a = this.storage, name = _a.name, version = _a.version;
                                    _d.label = 1;
                                case 1:
                                    _d.trys.push([1, 8, , 9]);
                                    this.eventBus.emit(StorageEvent.BootstrapStart);
                                    schema = this.getDatabaseSchema();
                                    return [4 /*yield*/, this.migrationManager.prepareAutoUpgradeHandler(name, this.engine)];
                                case 2:
                                    upgradeCallback = _d.sent();
                                    terminatedCallback = function () {
                                        _this.eventBus.emit(StorageEvent.Fetal, new DatabaseUnexpectedClosedError(name));
                                    };
                                    _d.label = 3;
                                case 3:
                                    _d.trys.push([3, 5, , 7]);
                                    _b = this;
                                    return [4 /*yield*/, this.engine.openDB(name, schema, version, {
                                            upgrade: upgradeCallback,
                                            terminated: terminatedCallback,
                                        })];
                                case 4:
                                    _b.db = _d.sent();
                                    return [3 /*break*/, 7];
                                case 5:
                                    error_1 = _d.sent();
                                    if (error_1.name !== 'VersionError' || !this.supportCompatibleOpen) {
                                        throw error_1;
                                    }
                                    _c = this;
                                    return [4 /*yield*/, this.engine.openDB(name, schema, undefined, {
                                            upgrade: upgradeCallback,
                                            terminated: terminatedCallback,
                                        })];
                                case 6:
                                    _c.db = _d.sent();
                                    return [3 /*break*/, 7];
                                case 7:
                                    this.db.setVersionChangeListener(function (oldVersion, newVersion) {
                                        _this.eventBus.emit(StorageEvent.VersionChange, { oldVersion: oldVersion, newVersion: newVersion });
                                    });
                                    this.eventBus.emit(StorageEvent.BootstrapEnd);
                                    this.eventBus.emit(StorageEvent.Ready, this.db);
                                    return [3 /*break*/, 9];
                                case 8:
                                    caughtErr_1 = _d.sent();
                                    this.reset();
                                    error = new DatabaseConnectError(name, caughtErr_1);
                                    this.eventBus.emit(StorageEvent.Error, error);
                                    this.eventBus.emit(StorageEvent.Fetal, error);
                                    throw error;
                                case 9: return [2 /*return*/];
                            }
                        });
                    }); })];
            });
        }); };
        this.handleModelRegistered = function (model) {
            if (_this.schemas.some(function (schema) { return schema.name === model.schema.name; })) {
                return;
            }
            var schema = model.schema;
            schema.dataVersion = _this.storage.version;
            _this.schemas.push(schema);
        };
        this.handleFetal = function () {
            _this.reset();
        };
        this.storage = storage;
        this.engine = options.engine;
        this.eventBus = options.eventBus;
        this.supportCompatibleOpen = options.supportCompatibleOpen;
        this.engine.setAspects(storage.aspects.engine);
        this.schemas = [SystemTableHelper.getSystemTableSchema()];
        if (options.databaseSchema) {
            var tableSchemas = JSON.parse(options.databaseSchema).collections.map(function (collection) {
                return __assign(__assign({}, collection), { dataVersion: _this.storage.version });
            });
            (_a = this.schemas).push.apply(_a, __spreadArray([], __read(tableSchemas)));
        }
        this.migrationManager = new MigrationManager(this.eventBus, this, storage.aspects.migration);
        this.eventBus.on(StorageEvent.RegisterModel, this.handleModelRegistered);
        this.eventBus.on(StorageEvent.Fetal, this.handleFetal);
    }
    DatabaseManager.prototype.getDatabaseEngine = function () {
        return this.engine;
    };
    DatabaseManager.prototype.getDatabaseSchema = function () {
        if (!this.storage.name || !this.storage.version) {
            throw new Error('The database name and version are required for generating dbSchema');
        }
        return {
            name: this.storage.name,
            version: this.storage.version,
            collections: this.schemas,
        };
    };
    DatabaseManager.prototype.closeDatabase = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, this.closingMutex.lock(function () { return __awaiter(_this, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    if (!this.db)
                                        return [2 /*return*/];
                                    return [4 /*yield*/, this.db.close()];
                                case 1:
                                    _a.sent();
                                    this.reset();
                                    this.eventBus.emit(StorageEvent.Closed);
                                    return [2 /*return*/];
                            }
                        });
                    }); })];
            });
        });
    };
    DatabaseManager.prototype.destroyDatabase = function () {
        var _this = this;
        return this.destroyingMutex.lock(function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.engine.deleteDB(this.storage.name)];
                    case 1:
                        _a.sent();
                        this.reset();
                        this.eventBus.emit(StorageEvent.Destroyed);
                        return [2 /*return*/];
                }
            });
        }); });
    };
    DatabaseManager.prototype.reset = function () {
        this.db = undefined;
    };
    return DatabaseManager;
}());
export default DatabaseManager;
//# sourceMappingURL=database-manager.js.map